import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import AdminLayout from '@/components/AdminLayout';
import { toast } from 'sonner';
import {
  Cloud, HardDrive, Activity, Image, FolderOpen, RefreshCw,
  ChevronLeft, ChevronRight, FileImage, FileVideo, FileText
} from 'lucide-react';

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let val = parseFloat(bytes);
  while (val >= 1024 && i < units.length - 1) { val /= 1024; i++; }
  return `${val.toFixed(2)} ${units[i]}`;
};

const formatNumber = (num) => {
  if (!num) return '0';
  return num.toLocaleString('tr-TR');
};

const calcPercent = (used, limit) => {
  if (!limit || limit === 0) return 0;
  return Math.min(100, (used / limit) * 100);
};

const ProgressBar = ({ used, limit, label, usedDisplay, limitDisplay, color = 'orange' }) => {
  const pct = calcPercent(used, limit);
  const colorMap = {
    orange: { bar: '#FF6600', bg: 'bg-orange-50', text: 'text-orange-700' },
    blue: { bar: '#3B82F6', bg: 'bg-blue-50', text: 'text-blue-700' },
    green: { bar: '#10B981', bg: 'bg-green-50', text: 'text-green-700' },
    purple: { bar: '#8B5CF6', bg: 'bg-purple-50', text: 'text-purple-700' },
  };
  const c = colorMap[color] || colorMap.orange;

  return (
    <div data-testid={`progress-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <span className="text-xs text-gray-500">{pct.toFixed(1)}%</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
        <div
          className="h-3 rounded-full transition-all duration-700 ease-out"
          style={{ width: `${pct}%`, backgroundColor: c.bar }}
        />
      </div>
      <div className="flex justify-between mt-1">
        <span className="text-xs text-gray-500">{usedDisplay} used</span>
        <span className="text-xs text-gray-500">{limitDisplay ? `of ${limitDisplay}` : 'Unlimited'}</span>
      </div>
    </div>
  );
};

const KPICard = ({ icon: Icon, title, value, subtitle, color = '#FF6600' }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow" data-testid={`kpi-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-start gap-4">
      <div className="p-3 rounded-lg" style={{ backgroundColor: `${color}15` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-500 mb-1">{title}</p>
        <p className="text-xl font-bold text-gray-900 truncate">{value}</p>
        {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
      </div>
    </div>
  </div>
);

const ResourceTypeIcon = ({ type, format }) => {
  if (type === 'video') return <FileVideo className="w-4 h-4 text-purple-500" />;
  if (type === 'raw' || format === 'pdf') return <FileText className="w-4 h-4 text-red-500" />;
  return <FileImage className="w-4 h-4 text-blue-500" />;
};

const CloudinaryDashboard = () => {
  const [usage, setUsage] = useState(null);
  const [resources, setResources] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [cursorHistory, setCursorHistory] = useState([]);
  const [resourceType, setResourceType] = useState('image');
  const [selectedFolder, setSelectedFolder] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const loadUsage = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/cloudinary/usage`);
      setUsage(res.data);
    } catch (err) {
      console.error('Cloudinary usage error:', err);
      toast.error('Failed to load Cloudinary usage data');
    }
  }, []);

  const loadFolders = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/cloudinary/folders`);
      setFolders(res.data.folders || []);
    } catch (err) {
      console.error('Cloudinary folders error:', err);
    }
  }, []);

  const loadResources = useCallback(async (cursor = null, type = resourceType, prefix = selectedFolder) => {
    setResourcesLoading(true);
    try {
      const params = new URLSearchParams({ resource_type: type, max_results: '20' });
      if (cursor) params.append('next_cursor', cursor);
      if (prefix) params.append('prefix', prefix);
      const res = await axios.get(`${API}/admin/cloudinary/resources?${params}`);
      setResources(res.data.resources || []);
      setNextCursor(res.data.next_cursor || null);
    } catch (err) {
      console.error('Cloudinary resources error:', err);
      toast.error('Failed to load resources');
    } finally {
      setResourcesLoading(false);
    }
  }, [resourceType, selectedFolder]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadUsage(), loadFolders(), loadResources()]);
      setLoading(false);
    };
    init();
  }, [loadUsage, loadFolders, loadResources]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([loadUsage(), loadResources()]);
    setRefreshing(false);
    toast.success('Dashboard refreshed');
  };

  const handleResourceTypeChange = (type) => {
    setResourceType(type);
    setCursorHistory([]);
    setNextCursor(null);
    loadResources(null, type, selectedFolder);
  };

  const handleFolderChange = (prefix) => {
    setSelectedFolder(prefix);
    setCursorHistory([]);
    setNextCursor(null);
    loadResources(null, resourceType, prefix);
  };

  const handleNextPage = () => {
    if (nextCursor) {
      setCursorHistory(prev => [...prev, nextCursor]);
      loadResources(nextCursor);
    }
  };

  const handlePrevPage = () => {
    if (cursorHistory.length > 0) {
      const prev = [...cursorHistory];
      prev.pop();
      setCursorHistory(prev);
      loadResources(prev.length > 0 ? prev[prev.length - 1] : null);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="flex flex-col items-center gap-3">
            <RefreshCw className="w-8 h-8 text-orange-500 animate-spin" />
            <p className="text-gray-500">Loading Cloudinary data...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8" data-testid="cloudinary-dashboard">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl font-bold text-gray-900" data-testid="cloudinary-dashboard-title">
                Cloudinary Dashboard
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Plan: <span className="font-semibold text-gray-700">{usage?.plan || '—'}</span>
                {usage?.last_updated && (
                  <span className="ml-3">Last updated: {new Date(usage.last_updated).toLocaleString('tr-TR')}</span>
                )}
              </p>
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <KPICard
              icon={HardDrive}
              title="Storage Used"
              value={usage?.storage?.used_display || '0 B'}
              subtitle={usage?.storage?.limit_display ? `Limit: ${usage.storage.limit_display}` : 'Unlimited'}
              color="#FF6600"
            />
            <KPICard
              icon={Activity}
              title="Bandwidth"
              value={usage?.bandwidth?.used_display || '0 B'}
              subtitle={usage?.bandwidth?.limit_display ? `Limit: ${usage.bandwidth.limit_display}` : 'Unlimited'}
              color="#3B82F6"
            />
            <KPICard
              icon={Image}
              title="Total Resources"
              value={formatNumber(usage?.resources)}
              subtitle={`+${formatNumber(usage?.derived_resources)} derived`}
              color="#10B981"
            />
            <KPICard
              icon={Cloud}
              title="Transformations"
              value={formatNumber(usage?.transformations?.used)}
              subtitle={usage?.transformations?.limit ? `Limit: ${formatNumber(usage.transformations.limit)}` : 'Unlimited'}
              color="#8B5CF6"
            />
          </div>

          {/* Usage Progress Bars */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8" data-testid="usage-progress-section">
            <h2 className="text-lg font-semibold text-gray-900 mb-5">Usage Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ProgressBar
                used={usage?.storage?.used_bytes}
                limit={usage?.storage?.limit_bytes}
                label="Storage"
                usedDisplay={usage?.storage?.used_display}
                limitDisplay={usage?.storage?.limit_display}
                color="orange"
              />
              <ProgressBar
                used={usage?.bandwidth?.used_bytes}
                limit={usage?.bandwidth?.limit_bytes}
                label="Bandwidth"
                usedDisplay={usage?.bandwidth?.used_display}
                limitDisplay={usage?.bandwidth?.limit_display}
                color="blue"
              />
              <ProgressBar
                used={usage?.transformations?.used}
                limit={usage?.transformations?.limit}
                label="Transformations"
                usedDisplay={formatNumber(usage?.transformations?.used)}
                limitDisplay={usage?.transformations?.limit ? formatNumber(usage.transformations.limit) : null}
                color="green"
              />
              <ProgressBar
                used={usage?.credits?.used}
                limit={usage?.credits?.limit}
                label="Credits"
                usedDisplay={usage?.credits?.used?.toFixed?.(2) || '0'}
                limitDisplay={usage?.credits?.limit ? usage.credits.limit.toFixed(2) : null}
                color="purple"
              />
            </div>
          </div>

          {/* Resources Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" data-testid="resources-section">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
              <h2 className="text-lg font-semibold text-gray-900">Resources</h2>
              <div className="flex items-center gap-3 flex-wrap">
                {/* Folder filter */}
                <select
                  value={selectedFolder}
                  onChange={(e) => handleFolderChange(e.target.value)}
                  className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white text-gray-700 focus:ring-2 focus:ring-orange-300 focus:border-orange-400 outline-none"
                  data-testid="folder-filter"
                >
                  <option value="">All Folders</option>
                  {folders.map(f => (
                    <option key={f.path} value={f.path}>{f.name}</option>
                  ))}
                </select>
                {/* Resource type filter */}
                <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden" data-testid="resource-type-filter">
                  {['image', 'video', 'raw'].map(t => (
                    <button
                      key={t}
                      onClick={() => handleResourceTypeChange(t)}
                      className={`px-3 py-2 text-sm font-medium capitalize transition-colors ${
                        resourceType === t
                          ? 'bg-orange-500 text-white'
                          : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                      data-testid={`filter-${t}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="resources-table">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left py-3 px-3 font-medium text-gray-500">Type</th>
                    <th className="text-left py-3 px-3 font-medium text-gray-500">Public ID</th>
                    <th className="text-left py-3 px-3 font-medium text-gray-500">Format</th>
                    <th className="text-right py-3 px-3 font-medium text-gray-500">Size</th>
                    <th className="text-left py-3 px-3 font-medium text-gray-500">Dimensions</th>
                    <th className="text-left py-3 px-3 font-medium text-gray-500">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {resourcesLoading ? (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-gray-400">
                        <RefreshCw className="w-5 h-5 animate-spin inline-block mr-2" />
                        Loading...
                      </td>
                    </tr>
                  ) : resources.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-gray-400">
                        No resources found
                      </td>
                    </tr>
                  ) : (
                    resources.map((r, idx) => (
                      <tr key={r.public_id || idx} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-3">
                          <ResourceTypeIcon type={r.resource_type} format={r.format} />
                        </td>
                        <td className="py-3 px-3">
                          <span className="text-gray-700 font-mono text-xs break-all max-w-[300px] inline-block" title={r.public_id}>
                            {r.public_id.length > 50 ? `...${r.public_id.slice(-47)}` : r.public_id}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          <span className="uppercase text-xs font-medium text-gray-500 bg-gray-100 rounded px-2 py-0.5">
                            {r.format || '—'}
                          </span>
                        </td>
                        <td className="py-3 px-3 text-right text-gray-600">{r.display_size}</td>
                        <td className="py-3 px-3 text-gray-600">
                          {r.width && r.height ? `${r.width}x${r.height}` : '—'}
                        </td>
                        <td className="py-3 px-3 text-gray-500 whitespace-nowrap">
                          {r.created_at ? new Date(r.created_at).toLocaleDateString('tr-TR') : '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-500">
                Showing {resources.length} resources
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrevPage}
                  disabled={cursorHistory.length === 0}
                  className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={handleNextPage}
                  disabled={!nextCursor}
                  className="p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  data-testid="next-page-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Folders Section */}
          {folders.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mt-8" data-testid="folders-section">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Folder Structure</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {folders.map(f => (
                  <button
                    key={f.path}
                    onClick={() => handleFolderChange(f.path)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm transition-all ${
                      selectedFolder === f.path
                        ? 'border-orange-400 bg-orange-50 text-orange-700 font-medium'
                        : 'border-gray-200 text-gray-600 hover:border-orange-300 hover:bg-orange-50/50'
                    }`}
                    data-testid={`folder-${f.name}`}
                  >
                    <FolderOpen className="w-4 h-4 flex-shrink-0" />
                    <span className="truncate">{f.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default CloudinaryDashboard;
